// schedule exports — a flat task list to .xlsx or .pdf, plus a print-friendly board view.
// heavy libs (xlsx / jsPDF) are lazily imported so they code-split out of the main bundle.

/* eslint-disable @typescript-eslint/no-explicit-any */

import { currentDatePrefs, excelDateFormat, formatDate, isIsoDay, toExcelDate, type DatePrefs } from '../lib/dates.js';

export interface SchedRow {
  vessel: string;
  sap: string;
  delivery: string;
  task: string;
  start: string;
  end: string;
  days: string;
  notes: string;
  status: string;
}

const HEAD = ['Vessel', 'SAP', 'Delivery', 'Task', 'Start', 'End', 'Days', 'Notes', 'Status'];
const asRow = (r: SchedRow) => [r.vessel, r.sap, r.delivery, r.task, r.start, r.end, r.days, r.notes, r.status];
const DATE_COLS = new Set([2, 4, 5]); // Delivery, Start, End

// spreadsheets get real dates (they sort and filter), shown in the user's chosen format
export async function exportScheduleXlsx(rows: SchedRow[], title: string, prefs: DatePrefs = currentDatePrefs()): Promise<string> {
  const XLSX: any = await import('xlsx');
  const body = rows.map((r) => asRow(r).map((x, i) => (DATE_COLS.has(i) && isIsoDay(x) ? toExcelDate(x) : x)));
  const ws = XLSX.utils.aoa_to_sheet([HEAD, ...body], { dateNF: excelDateFormat(prefs) });
  ws['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 6 }, { wch: 32 }, { wch: 10 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Schedule');
  const filename = `${title}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
}

export async function exportSchedulePdf(rows: SchedRow[], title: string, prefs: DatePrefs = currentDatePrefs(), heading = title): Promise<string> {
  const { jsPDF }: any = await import('jspdf');
  const autoTable: any = (await import('jspdf-autotable')).default;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  doc.setFontSize(14);
  doc.text(heading, 40, 40);
  autoTable(doc, {
    head: [HEAD],
    body: rows.map((r) => asRow(r).map((x, i) => (DATE_COLS.has(i) ? formatDate(x, prefs) || x : x))),
    startY: 56,
    styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
    headStyles: { fillColor: [28, 40, 49] },
    columnStyles: { 0: { cellWidth: 150 }, 7: { cellWidth: 170 } },
  });
  const filename = `${title}.pdf`;
  doc.save(filename);
  return filename;
}

/** open a blank print popup. MUST be called synchronously inside the user's click — browsers block
 *  window.open() once the click's call stack has yielded to an await, so a caller that needs to fetch
 *  data first should open the window up-front and hand it to renderPrint() when the data arrives. */
export function openPrintWindow(): Window | null {
  return window.open('', '_blank', 'width=1200,height=850');
}

/** write the board HTML into an already-open print window and trigger the print dialog
 *  (which also offers "Save as PDF"). */
export function renderPrint(w: Window, title: string, bodyHtml: string): void {
  w.document.open();
  w.document.write(
    `<!doctype html><html><head><title>${title}</title><style>
      *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
      body{font-family:Arial,Helvetica,sans-serif;margin:20px;color:#111}
      h1{font-size:16px;margin:0 0 2px} .sub{color:#666;font-size:11px;margin-bottom:12px}
      table{border-collapse:collapse;width:100%;font-size:10px;table-layout:fixed}
      th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;vertical-align:top;word-wrap:break-word}
      th{background:#1C2831;color:#fff}
      tr.green td,tr.ok td{background:#d8efdf} tr.warn td{background:#f6ecc8} tr.err td{background:#f6dcdc}
      tr.info td{background:#dbe7f6} tr.gray td{background:#eceef1}
      td small{color:#666}
      .bar{display:inline-block;background:#e6eef2;border:1px solid #cfdae0;border-radius:3px;padding:1px 4px;margin:1px;font-size:9px}
      .bar.done{text-decoration:line-through;opacity:.55}
      @page{size:landscape;margin:12mm}
    </style></head><body>${bodyHtml}<script>window.onload=function(){setTimeout(function(){window.print();},150);};</script></body></html>`,
  );
  w.document.close();
}

/** open a clean printable window with the given board HTML and trigger the print dialog. use this
 *  when the body HTML is ready synchronously; if you must await first, use openPrintWindow +
 *  renderPrint so the popup isn't opened after the gesture (and blocked). Returns false when the
 *  browser blocked the pop-up, so the caller can say so. */
export function printSchedule(title: string, bodyHtml: string): boolean {
  const w = openPrintWindow();
  if (!w) return false;
  renderPrint(w, title, bodyHtml);
  return true;
}
